import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $cardsListStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, FormatUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import type { CardsResponse } from '@shared/api/rest/cards';
import type { UsersResponse, UserWithPaymentInfo } from '@shared/api/rest/users';
import { Button, Modal, Multiselect, Select } from '@shared/widgets';

interface IAddCardsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UsersResponse;
  cardsItems: CardsResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; cardIds: number[] }) => void;
}

export const AddCardsModal = (props: IAddCardsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);
  const userCardsStore = useUnit($cardsListStore.store);

  const [accountList, setAccountList] = useState<AccountResponse[]>([]);
  const [selected, setSelected] = useState<number | undefined>();
  const [selectedCard, setSelectedCard] = useState<number[]>([]);

  useEffect(() => {
    if (!props.isOpen) {
      setSelected(undefined);
      setSelectedCard([]);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (profileFeatureStore.data?.accountFlows) {
      const result = AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
      result && setAccountList(result);
    }
  }, [userAccountsStore.data]);

  useEffect(() => {
    if (!selected) return;
    $cardsListStore.events.getUserCards({ AccountId: selected, Count: 100 });
  }, [selected]);

  const addCards = () => {
    props.onSubmit({
      userIds: props.user.id,
      cardIds: selectedCard,
    });
    setSelected(undefined);
    setSelectedCard([]);
    props.onClose();
  };

  const accountCardsList = useMemo(
    () => (accountList.length > 0 ? accountList.filter((account) => account.type === AccountType.Card) : []),
    [accountList],
  );

  return (
    <Modal title='Add cards' isOpen={props.isOpen} onClose={props.onClose}>
      <div className='mb-4'>
        <Select
          isLoading={userAccountsStore.loading}
          disabled={userAccountsStore.loading}
          items={accountCardsList.map((acc) => ({
            value: acc.id,
            label: FormatUtils.toSliceNumber(acc.accountNumber),
          }))}
          value={selected}
          nullValue={{ isEnable: false, text: 'Choose account' }}
          onChange={setSelected}
        />
      </div>
      <Multiselect
        isLoading={userCardsStore.loading}
        disabled={!selected || userCardsStore.loading}
        items={userCardsStore.data
          .filter((c) => !props.cardsItems.map((uc) => uc.cardId).includes(c.cardId))
          .map((c) => ({ value: c.cardId, label: FormatUtils.toSliceNumber(c.cardNumber, 16) }))}
        nullValue={{ isEnable: false, text: 'Choose cards' }}
        value={selectedCard}
        onChange={(val) => setSelectedCard(val!)}
      />
      <div className='mt-6'>
        <Button color='primary' fullWidth onClick={() => addCards()} disabled={selectedCard.length === 0}>
          Add
        </Button>
      </div>
    </Modal>
  );
};
